import React, {FC, useState} from 'react';

import {ROUTE} from 'containers/App/constants';
import {resetPassword} from 'auth/api';

import loginImage from 'assets/images/login.png';

import {
  Background,
  Label,
  Title,
  PrimaryButton,
  ErrorMessage,
  WarningMessage,
  LinkButton,
} from 'components';
import LoginDisclaimer from 'containers/LoginPage/LoginDisclaimer';

import {makeBorderClass} from 'components/Form/Input';
import {
  I18N_ENTER_EMAIL,
  I18N_PASSWORD_RESET_EMAIL_LABEL,
  I18N_PASSWORD_RESET_SEND_LABEL,
  I18N_PASSWORD_RESET_LOGIN_LABEL,
  I18N_PASSWORD_RESET_TITLE,
  I18N_RESET_REQUEST_SENT,
  I18N_UNKNOWN_EMAIL,
} from './i18n';

import {useForm, RegisterOptions} from 'react-hook-form';
import {validate as isEmail} from 'isemail';
import {useQuery} from './hooks';

type Inputs = {
  email: string;
};

const inputRequirements: Record<keyof Inputs, RegisterOptions> = {
  email: {
    required: true,
    validate: value => isEmail(value),
  },
};

const ResetPassword: FC = () => {
  // Query parameter retreival
  const query = useQuery();
  const emailPrefill = query.get('email') || '';

  // Local state
  const [submitted, setSubmitted] = useState(false);
  const [unknownEmail, setUnknownEmail] = useState(false);

  const {register, handleSubmit, errors} = useForm<Inputs>();

  const onSubmit = ({email}: Inputs) => {
    resetPassword(email)
      .then(() => {
        setSubmitted(true);
        setUnknownEmail(false);
      })
      .catch(() => {
        setUnknownEmail(true);
        setSubmitted(false);
      });
  };

  return (
    <>
      <Background>
        <div className="pt-24 lg:pt-0 lg:w-1/3 mx-auto flex flex-col justify-center">
          <img src={loginImage} className="mx-auto -mb-16" alt="Login" />
          <form
            className="bg-white rounded-gigantic py-6 px-10 flex flex-col z-index-50 relative"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Title */}
            <Title className="text-3xl">{I18N_PASSWORD_RESET_TITLE()}</Title>

            {/* Input Fields */}
            <Label
              htmlFor="email"
              label={I18N_PASSWORD_RESET_EMAIL_LABEL()}
              className="mt-8"
            >
              <input
                className={`${makeBorderClass()} p-2 pl-4 w-full`}
                id="email"
                name="email"
                type="email"
                defaultValue={emailPrefill}
                placeholder="email"
                ref={register(inputRequirements.email)}
              />
            </Label>

            {/* Form validation error messages */}
            {errors.email && errors.email.type === 'required' && (
              <ErrorMessage className="mt-4">{I18N_ENTER_EMAIL()}</ErrorMessage>
            )}

            {/* Submission button */}
            <PrimaryButton type="submit" className="w-full h-12 mt-4">
              {I18N_PASSWORD_RESET_SEND_LABEL()}
            </PrimaryButton>

            {/* Unknown email error */}
            {unknownEmail && (
              <ErrorMessage className="mt-4">
                {I18N_UNKNOWN_EMAIL()}
              </ErrorMessage>
            )}

            {/* Submission success alert */}
            {submitted && (
              <WarningMessage className="mt-4">
                {I18N_RESET_REQUEST_SENT()}
              </WarningMessage>
            )}

            {/* Redirect to login page button */}
            <div className="text-right mt-4">
              <LinkButton to={ROUTE.Index}>
                {I18N_PASSWORD_RESET_LOGIN_LABEL()}
              </LinkButton>
            </div>
          </form>
          <LoginDisclaimer />
        </div>
      </Background>
    </>
  );
};

export default ResetPassword;
